import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../config/axiosConfig';
import { setRealtimeEvent } from "../../realtime/realtimeSlice";
import { fetchEmployeeAuthData, logout } from './leadsSlice';
import {
  addItemToList,
  getRealtimeEntityFromPayload,
  isModuleMatch,
  removeItemFromList,
  updateItemInList,
} from "../../realtime/realtimeUtils";

const resolveEntityId = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    const id = value._id ?? value.id;
    return id != null ? String(id) : "";
  }
  return String(value);
};

/** L3 (salesperson): only projects created by the logged-in user. */
const filterProjectsForAuthLevel = (allProjects, authLevel, loggedInUserId) => {
  if (authLevel !== "L3") return allProjects;
  const userId = resolveEntityId(loggedInUserId);
  if (!userId) return allProjects;
  return (allProjects || []).filter(
    (project) => resolveEntityId(project.createdBy) === userId
  );
};

export const fetchAllProjects = createAsyncThunk(
  'projects/fetchAll',
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get('/managerProjectRequested');
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch projects');
    }
  }
);

export const fetchProjectByQuotationId = createAsyncThunk(
  'projects/fetchByQuotationId',
  async (quotationId, thunkAPI) => {
    try {
      const response = await axiosInstance.get(`/managerProjectRequested/quotationId/${quotationId}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch project by quotationId');
    }
  }
);

export const createProjects = createAsyncThunk(
  'projects/create',
  async (projectData, thunkAPI) => {
    try {
      const response = await axiosInstance.post('/managerProjectRequested/create', projectData);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to create project');
    }
  }
);

export const updateProjects = createAsyncThunk(
  'projects/update',
  async (projectData, thunkAPI) => {
    try {
      const response = await axiosInstance.put('/managerProjectRequested/update', projectData);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to update project');
    }
  }
);

export const updateProjectByName = createAsyncThunk(
  'projects/updateByName',
  async (formData, thunkAPI) => {
    try {
      const response = await axiosInstance.put(
        '/managerProjectRequested/updateByName',
        formData, // FormData sent here
        {
          headers: {
            'Content-Type': 'multipart/form-data', // Let browser handle boundaries
          },
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to update by name');
    }
  }
);


export const deleteProject = createAsyncThunk(
  'projects/delete',
  async (projectId, thunkAPI) => {
    try {
      const response = await axiosInstance.delete(`/managerProjectRequested/${projectId}`);
      return { id: projectId, message: response.data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to delete project');
    }
  }
);

const initialState = {
  projects: [],
  allProjects: [],
  projectsbyname: [],
  authLevel: null,
  loggedInUserId: null,
  loading: false,
  error: null,
};

const projectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

     .addCase(fetchAllProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllProjects.fulfilled, (state, action) => {
        state.loading = false;
        const payload = Array.isArray(action.payload) ? action.payload : [];
        state.allProjects = payload;
        state.projects = filterProjectsForAuthLevel(
          payload,
          state.authLevel,
          state.loggedInUserId
        );
      })
      .addCase(fetchAllProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchProjectByQuotationId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectByQuotationId.fulfilled, (state, action) => {
        state.loading = false;
        state.projectsbyname = [action.payload];
      })
      .addCase(fetchProjectByQuotationId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProjects.fulfilled, (state, action) => {
        state.loading = false;
        const created = Array.isArray(action.payload) ? action.payload : [action.payload];
        state.allProjects.push(...created);
        state.projects = filterProjectsForAuthLevel(
          state.allProjects,
          state.authLevel,
          state.loggedInUserId
        );
      })
      .addCase(createProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateProjects.fulfilled, (state, action) => {
        state.loading = false;
        const applyUpdates = (arr) =>
          arr.map((proj) =>
            action.payload.find((updated) => updated._id === proj._id) || proj
          );
        state.allProjects = applyUpdates(state.allProjects);
        state.projects = filterProjectsForAuthLevel(
          state.allProjects,
          state.authLevel,
          state.loggedInUserId
        );
      })

      .addCase(updateProjectByName.fulfilled, (state, action) => {
        state.loading = false;
        // Update logic based on project name
      })

      .addCase(deleteProject.fulfilled, (state, action) => {
        state.loading = false;
        state.allProjects = state.allProjects.filter(p => p._id !== action.payload.id);
        state.projects = state.projects.filter(p => p._id !== action.payload.id);
      })
      .addCase(setRealtimeEvent, (state, action) => {
        const { module, action: eventAction, data } = action.payload || {};
        if (!isModuleMatch(module, ["project", "managerprojectrequested"])) return;

        const incomingItem = getRealtimeEntityFromPayload(data);
        if (!incomingItem) return;

        if (eventAction === "updated") {
          state.allProjects = updateItemInList(state.allProjects, incomingItem);
          state.projects = filterProjectsForAuthLevel(
            state.allProjects,
            state.authLevel,
            state.loggedInUserId
          );
          state.projectsbyname = updateItemInList(state.projectsbyname, incomingItem);
        }

        if (eventAction === "created") {
          state.allProjects = addItemToList(state.allProjects, incomingItem);
          state.projects = filterProjectsForAuthLevel(
            state.allProjects,
            state.authLevel,
            state.loggedInUserId
          );
          state.projectsbyname = addItemToList(state.projectsbyname, incomingItem);
        }

        if (eventAction === "deleted") {
          state.allProjects = removeItemFromList(state.allProjects, incomingItem);
          state.projects = removeItemFromList(state.projects, incomingItem);
          state.projectsbyname = removeItemFromList(state.projectsbyname, incomingItem);
        }
      })

      .addCase(fetchEmployeeAuthData.fulfilled, (state, action) => {
        state.authLevel = action.payload.authLevel;
        state.loggedInUserId = action.payload.loggedInUserId ?? null;
        state.projects = filterProjectsForAuthLevel(
          state.allProjects,
          state.authLevel,
          state.loggedInUserId
        );
      })
      .addCase(logout, (state) => {
        state.projects = [];
        state.allProjects = [];
        state.authLevel = null;
        state.loggedInUserId = null;
      })

      .addMatcher((action) => action.type.endsWith('/rejected'), (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default projectSlice.reducer;
